import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer/Footer';
import NewsPopup from '../NewsPopup/NewsPopup';
import ExtraCopyRequestsModal from '../ExtraCopyRequestsModal/ExtraCopyRequestsModal';
import ExtraCopyNotificationModal from '../ExtraCopyNotificationModal/ExtraCopyNotificationModal';
import ReminderResponseNotificationModal from '../ReminderResponseNotificationModal/ReminderResponseNotificationModal';
import AuditLogCriticalModal from '../AuditLogCriticalModal/AuditLogCriticalModal';
import VoucherRequestModal from '../VoucherRequestModal/VoucherRequestModal';
import VoucherManualRequestsModal from '../VoucherManualRequestsModal/VoucherManualRequestsModal';
import { useAuth } from '../../hooks/useAuth';
import { useNewsPopup } from '../../hooks/useNewsPopup';
import { useImeiReminderBadge } from '../../hooks/useImeiReminderPopup';
import { getSocket } from '../../services/socket';
import { useExtraCopyRequests } from '../../hooks/useExtraCopyRequests';
import { useExtraCopyNotification } from '../../hooks/useExtraCopyNotification';
import { useReminderResponseNotification } from '../../hooks/useReminderResponseNotification';
import { useVoucherManualRequests } from '../../hooks/useVoucherManualRequests';
import { useAuditLogCriticalNotifications } from '../../hooks/useAuditLogCriticalNotifications';
import './Layout.scss';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Socket früh verbinden, damit Echtzeit-Updates (z.B. Excel-Upload durch Büro) bei allen funktionieren
  useEffect(() => {
    if (user?.id) getSocket();
  }, [user?.id]);

  useEffect(() => {
    const host = window.location.hostname.toLowerCase();
    const isSchnelltest =
      host === 'az-schnelltest.berlin' || host === 'www.az-schnelltest.berlin';
    const cls = 'layout-host--schnelltest';
    if (isSchnelltest) {
      document.body.classList.add(cls);
    }
    return () => {
      document.body.classList.remove(cls);
    };
  }, []);
  const [showExtraCopyModal, setShowExtraCopyModal] = useState(false);
  const [showExtraCopyNotificationModal, setShowExtraCopyNotificationModal] = useState(false);
  const [showReminderResponseModal, setShowReminderResponseModal] = useState(false);
  const [showVoucherRequestModal, setShowVoucherRequestModal] = useState(false);
  const [showVoucherManualRequestsModal, setShowVoucherManualRequestsModal] = useState(false);
  const [showAuditLogCriticalModal, setShowAuditLogCriticalModal] = useState(false);
  const { showPopup, content, onMarkAsRead } = useNewsPopup();
  const { hasUnreadReminders, reminderCount } = useImeiReminderBadge();
  const { requests, hasPendingRequests, requestCount, loading, approve, reject } = useExtraCopyRequests();
  const { notifications, hasUnreadNotifications, notificationCount, markAsRead } = useExtraCopyNotification();
  const { notifications: reminderResponseNotifications, hasUnreadNotifications: hasReminderResponseNotifications, notificationCount: reminderResponseCount, markAsRead: markReminderResponseRead } = useReminderResponseNotification();
  const voucherManualReq = useVoucherManualRequests();
  const auditCritical = useAuditLogCriticalNotifications();

  const openVerlauf = () => {
    navigate('/imeis?showVerlauf=1');
  };

  useEffect(() => {
    if (hasUnreadNotifications) setShowExtraCopyNotificationModal(true);
  }, [hasUnreadNotifications]);

  useEffect(() => {
    if (hasReminderResponseNotifications) setShowReminderResponseModal(true);
  }, [hasReminderResponseNotifications]);

  useEffect(() => {
    if (auditCritical.hasUnreadNotifications) setShowAuditLogCriticalModal(true);
  }, [auditCritical.hasUnreadNotifications]);

  return (
    <div className="app">
      <Navbar
        hasReminderBadge={hasUnreadReminders}
        reminderCount={reminderCount}
        onOpenVerlauf={openVerlauf}
        hasExtraCopyBadge={hasPendingRequests}
        extraCopyCount={requestCount}
        onOpenExtraCopyModal={() => setShowExtraCopyModal(true)}
        hasExtraCopyResultBadge={hasUnreadNotifications}
        extraCopyResultCount={notificationCount}
        onOpenExtraCopyResultModal={() => setShowExtraCopyNotificationModal(true)}
        hasReminderResponseBadge={hasReminderResponseNotifications}
        reminderResponseCount={reminderResponseCount}
        onOpenReminderResponseModal={() => setShowReminderResponseModal(true)}
        hasVoucherManualRequestBadge={voucherManualReq.hasPendingRequests}
        voucherManualRequestCount={voucherManualReq.requestCount}
        onOpenVoucherManualRequestsModal={() => setShowVoucherManualRequestsModal(true)}
        onOpenVoucherRequestModal={() => setShowVoucherRequestModal(true)}
        hasAuditCriticalBadge={auditCritical.hasUnreadNotifications}
        auditCriticalCount={auditCritical.notificationCount}
        onOpenAuditCriticalModal={() => setShowAuditLogCriticalModal(true)}
      />
      <main className="main">
        <div className="container">
          {children}
        </div>
      </main>
      <Footer />
      {showPopup && (
        <NewsPopup content={content} onMarkAsRead={onMarkAsRead} />
      )}
      <ExtraCopyRequestsModal
        isOpen={showExtraCopyModal}
        onClose={() => setShowExtraCopyModal(false)}
        requests={requests}
        loading={loading}
        onApprove={approve}
        onReject={reject}
      />
      <ExtraCopyNotificationModal
        isOpen={showExtraCopyNotificationModal}
        onClose={() => setShowExtraCopyNotificationModal(false)}
        notifications={notifications}
        onMarkAsRead={markAsRead}
      />
      <ReminderResponseNotificationModal
        isOpen={showReminderResponseModal}
        onClose={() => setShowReminderResponseModal(false)}
        notifications={reminderResponseNotifications}
        onMarkAsRead={markReminderResponseRead}
      />
      <VoucherRequestModal
        isOpen={showVoucherRequestModal}
        onClose={() => setShowVoucherRequestModal(false)}
        onSuccess={(res) => {
          if (res?.message) window.alert(res.message);
        }}
      />
      <VoucherManualRequestsModal
        isOpen={showVoucherManualRequestsModal}
        onClose={() => setShowVoucherManualRequestsModal(false)}
        requests={voucherManualReq.requests}
        loading={voucherManualReq.loading}
        onApprove={async (id) => {
          try {
            await voucherManualReq.approve(id);
          } catch (e) {
            console.error(e);
            window.alert(e.response?.data?.message || e.message || 'Genehmigen fehlgeschlagen');
          }
        }}
        onReject={async (id) => {
          try {
            await voucherManualReq.reject(id);
          } catch (e) {
            console.error(e);
            window.alert(e.response?.data?.message || e.message || 'Ablehnen fehlgeschlagen');
          }
        }}
      />
      <AuditLogCriticalModal
        isOpen={showAuditLogCriticalModal}
        onClose={() => setShowAuditLogCriticalModal(false)}
        notifications={auditCritical.notifications}
        onMarkAsRead={auditCritical.markAsRead}
      />
    </div>
  );
};

export default Layout;
